import {
	expressAddClass,
	expressQuerySelector,
	expressRemoveClass,
	expressEventListener,
	hasClass,
	isElementPartiallyVisibleInViewport,
} from "../../common/html";
import {
	createGridHeaderControls,
	IElementActivationOptions,
	IGridHeaderControls,
} from "../molecules/collection-grid-header";
import { createGridBody, IGridBody, IPageLoadedResult } from "../molecules/collection-grid-body";
import { createPageLog } from "../molecules/page-log";
import { getParameter, queryRemoveKeys, updateParameter } from "../../utils/querystring";
import { StoreType } from "../../services/storageService";

export enum GridMode {
	Album = 0,
	Photo = 1,
}

export interface ISortValues {
	sortDirection: number;
	folderColumns: number;
}

export interface IGrid {
	readonly getStoreType: () => StoreType;
	readonly refresh: (forceUpdateGridScrollPosition: boolean) => Promise<void>;
	readonly getSelectedItems: () => HTMLElement[];
	readonly getSelectedItemIds: () => string[];
	readonly isHideImportedChecked: () => boolean;
	readonly toggleAllImportedMessage: (showMessage: boolean) => void;
	readonly allPagesLoaded: () => boolean;
	readonly getItems: () => HTMLElement[];
	readonly enableItem: (item: HTMLElement) => void;
	readonly disableItem: (item: HTMLElement) => void;
	readonly isItemEnabled: (item: HTMLElement) => boolean;
	readonly isItemDisabled: (item: HTMLElement) => boolean;
	readonly dispose: () => void;
	readonly updateGridScrollPosition: (force?: boolean) => void;
}

export interface ICreateGridDependencies {
	pageLogEl?: HTMLElement;
	usePlusSignToSelectAll?: boolean;
	maxSelectableItems?: number;
	readonly orderPrints?: (
		selectedItems: HTMLElement[],
		selectAll: boolean,
		bulkUrl: string,
		maxAmountOfPhotos: number,
		useDeselected: boolean,
		totalAmountOfItems: number,
	) => void;
	readonly onDelete?: (
		itemIdsToDelete: string[],
		useDeselected: boolean,
		nrOfSelectedItems: number,
		allSelected: boolean,
		refresh: () => Promise<void>,
		onDeleteAll: () => void,
	) => Promise<void>;
	readonly fetchGridBody: (
		sortValue: string,
		currentPage: number,
		storeType: StoreType,
		maxSelectableItems: number,
	) => Promise<string>;
	readonly onMove?: (selectedItemIds: string[], useDeselected: boolean, allSelected: boolean) => Promise<void>;
	readonly onItemClick?: (itemId: string, item: HTMLElement, sortValue: string, refresh: () => Promise<void>) => void;
	readonly onItemsSelect?: (itemIds: string[], items: HTMLElement[], isSelected: boolean) => void;
	readonly onNewItemClick?: (refresh: () => Promise<void>) => void;
	readonly fetchItems: (
		currentPage: number,
		store: StoreType,
		pageToken: string,
		sortValue?: string,
	) => Promise<string>;
	readonly fetchItemAmount?: () => Promise<number>;
	readonly enableOrderPrintsButton?: (selectedItemsCount: number) => IElementActivationOptions;
	readonly importPhotos?: (
		selectedItems: HTMLElement[],
		selectAll: boolean,
		useDeselected: boolean,
		totalAmountOfItems: number,
		store: StoreType,
	) => void;
	readonly getImportedPhotosIds?: (store: StoreType) => string[];
	readonly updateOrSetPageTitle?: (label: string) => void;
	readonly itemsLoaded?: (store: StoreType) => void;
	readonly onImportedPhotosCheckboxChanged?: (store: StoreType) => void;
	readonly afterGridBodyLoaded?: (gridBody: IGridBody) => void;
}

export async function createGrid(
	gridEl: HTMLElement,
	pageHeaderEl: HTMLElement,
	deps: ICreateGridDependencies,
): Promise<IGrid> {
	let gridBodyEl = expressQuerySelector<HTMLElement>(gridEl, ".technical-grid-body", true);
	const gridHeaderEl = expressQuerySelector<HTMLElement>(gridEl, ".technical-grid-controls", false);
	const sourceHeader = expressQuerySelector<HTMLElement>(pageHeaderEl, ".technical-grid-header-selectsource", false);
	const actionButton = expressQuerySelector<HTMLElement>(gridEl, ".technical-action-button", false);
	if (actionButton) {
		expressEventListener(actionButton, "click", (ev: MouseEvent) => {
			if (hasClass(actionButton, "a-button--disabled")) {
				ev.preventDefault();
				ev.stopPropagation();
			}
		});
	}
	const {
		pageLogEl,
		maxSelectableItems,
		orderPrints,
		onDelete,
		fetchGridBody,
		onMove,
		onItemClick,
		onItemsSelect,
		onNewItemClick,
		fetchItems,
		fetchItemAmount,
		enableOrderPrintsButton,
		importPhotos,
		getImportedPhotosIds,
		updateOrSetPageTitle,
		itemsLoaded,
		usePlusSignToSelectAll,
		onImportedPhotosCheckboxChanged,
		afterGridBodyLoaded,
	} = deps;
	let gridHeaderControls: IGridHeaderControls;
	let gridBody: IGridBody;
	let isObserver: IntersectionObserver;
	const pageLog = pageLogEl && createPageLog(pageLogEl);
	const storeType = StoreType[gridBodyEl.dataset.store] || StoreType.eXpress;
	let loadingEl: HTMLElement;
	let toggleAllImportedMssEl: HTMLElement;

	let nrOfTotalItems = parseInt(gridEl.dataset.total, 10);
	const updateTotalItems = (newTotal: number) => {
		nrOfTotalItems = newTotal;
		gridEl.dataset.total = newTotal.toString();

		if (gridHeaderControls?.updateAlbumInfo) {
			gridHeaderControls.updateAlbumInfo(newTotal);
		}
	};

	let useDeselected = false;
	const allPagesLoaded = () => {
		return nrOfTotalItems > -1 && gridBody.getItems().length >= nrOfTotalItems;
	};

	const onOrderPrints = (bulkUrl: string, maxAmountOfPhotos: number) => {
		if (pageLog) pageLog.hideLog();
		const selectedItems = useDeselected ? gridBody.getDeselectedItems() : gridBody.getSelectedItems();

		const isAllSelected = gridHeaderControls?.isSelectAllActive() || getNrOfSelectedItems() == nrOfTotalItems;

		if (orderPrints)
			orderPrints(selectedItems, isAllSelected, bulkUrl, maxAmountOfPhotos, useDeselected, nrOfTotalItems);
		if (importPhotos) importPhotos(selectedItems, isAllSelected, useDeselected, nrOfTotalItems, storeType);
	};

	const repositionGrid = () => {
		if (!sourceHeader) return;

		const sourceHeaderHeight = sourceHeader.getBoundingClientRect().height;
		let headerAndControlsHeight = sourceHeaderHeight;

		if (gridHeaderEl) {
			gridHeaderEl.style.top = sourceHeaderHeight + "px";
			headerAndControlsHeight += gridHeaderEl.getBoundingClientRect().height;
		}

		gridBodyEl.style.paddingTop = headerAndControlsHeight + "px";
	};

	const onDeleteAll = () => {
		// Remove all items
		gridBody.removeAllItems();
		updateTotalItems(0);

		// Reset header
		gridHeaderControls?.reset();
		gridHeaderControls?.updateAlbumInfo(0);

		hideLoading();
		const url = queryRemoveKeys(["currentpage"]);
		history.replaceState(null, "", url.toLowerCase());
	};

	const onDeleteEvent = () => {
		if (pageLog) pageLog.hideLog();

		const itemIds = useDeselected ? gridBody.getDeselectedItemIds() : gridBody.getSelectedItemIds();

		const selectAll =
			(itemIds.length <= 0 || itemIds.length == nrOfTotalItems) && gridHeaderControls?.isSelectAllActive(true);

		return selectAll
			? onDelete([], true, getNrOfSelectedItems(), selectAll, refresh, onDeleteAll)
			: onDelete(itemIds, useDeselected, getNrOfSelectedItems(), selectAll, refresh, onDeleteAll);
	};

	const selectAllClicked = (isSelectAllActive: boolean): void => {
		if (pageLog) pageLog.hideLog();
		if (gridBody && gridBody.selectAllItems) gridBody.selectAllItems(isSelectAllActive);
	};

	const onItemClickEvent = (itemId: string, item: HTMLElement) => {
		if (pageLog) pageLog.hideLog();
		if (onItemClick) onItemClick(itemId, item, gridHeaderControls?.getSortValue(), refresh);
	};

	const onItemsSelectEvent = (itemIds: string[], items: HTMLElement[], isSelected: boolean) => {
		if (pageLog) pageLog.hideLog();
		if (!gridHeaderControls) return;

		// Deselected an item => deselect SelectAll
		if (allPagesLoaded() && getNrOfSelectedItems() == 0 && getNrOfSelectableItems() > 0)
			gridHeaderControls.markSelectAllAsActive(false);

		updateSelectAllState();

		onItemsSelect && onItemsSelect(itemIds, items, isSelected);
	};

	const getNrOfSelectableItems = () => {
		return gridBody.getEnabledItems().length;
	};

	const getNrOfVisuallySelectedItems = () => {
		return gridBody.getSelectedItemIds().length;
	};

	const getNrOfSelectedItems = () => {
		const isSelectAllActive = gridHeaderControls.isSelectAllActive();
		const nrOfLoadedItems = gridBody.getItems().length;

		const nrOfSelectedIds = gridBody.getSelectedItemIds().length;
		const nrOfDeSelectedIds = gridBody.getDeselectedItemIds().length;

		if (nrOfTotalItems === nrOfLoadedItems) useDeselected = nrOfDeSelectedIds < nrOfSelectedIds;
		else useDeselected = isSelectAllActive;

		// -1 = unknown nr of items
		if (isSelectAllActive && nrOfTotalItems == -1) return gridBody.getSelectedItemIds().length;

		// selectAll and already imported => only count visually selected
		if (isSelectAllActive && getImportedPhotosIds && !!getImportedPhotosIds(storeType).length)
			return gridBody.getSelectedItemIds().length;

		// selectAll => count all possible items
		if (isSelectAllActive) return useDeselected ? nrOfTotalItems - nrOfDeSelectedIds : nrOfSelectedIds;

		// count all actually selected items
		return gridBody.getSelectedItemIds().length;
	};

	const updateSelectAllState = () => {
		if (!gridHeaderControls) return;

		const isSelectAllActive = gridHeaderControls.isSelectAllActive();

		let showPlusIcon = false;
		if (usePlusSignToSelectAll && isSelectAllActive) {
			showPlusIcon = nrOfTotalItems < 0; // -1 (= unknown itemcount) => always show plus icon
			if (!showPlusIcon) {
				const hasImportedPhotos = getImportedPhotosIds && !!getImportedPhotosIds(storeType).length;
				showPlusIcon = hasImportedPhotos && !allPagesLoaded();
			}
		}

		gridHeaderControls.setSelectAllState(showPlusIcon);
	};

	const onNewItemClickEvent = () => {
		if (pageLog) pageLog.hideLog();
		onNewItemClick(refresh);
	};

	const onMoveEvent = () => {
		if (pageLog) pageLog.hideLog();
		const itemIds = useDeselected ? gridBody.getDeselectedItemIds() : gridBody.getSelectedItemIds();

		return onMove(itemIds, useDeselected, gridHeaderControls?.isSelectAllActive());
	};

	const loadItems = (currentPage: number, store: StoreType, pageToken: string) => {
		if (pageLog) pageLog.hideLog();
		return fetchItems(currentPage, store, pageToken, gridHeaderControls?.getSortValue());
	};

	const onSort = (sortValue: string) => {
		gridHeaderControls?.reset();
		return refresh(sortValue);
	};

	const refresh = async (sortValue?: string, forceUpdateGridScrollPosition: boolean = false) => {
		expressAddClass(gridBodyEl, "skeleton-loading");
		if (pageLog) pageLog.hideLog();

		if (!sortValue) sortValue = gridHeaderControls?.getSortValue();

		let maxSelectableItems = 0;
		if (gridBodyEl.dataset.maxselectableitems) maxSelectableItems = parseInt(gridBodyEl.dataset.maxselectableitems);

		return fetchGridBody(sortValue, 1, storeType, maxSelectableItems).then(async (res) => {
			if (!res) return;

			dispose();

			gridBodyEl.outerHTML = res;
			gridBodyEl = expressQuerySelector<HTMLElement>(gridEl, ".technical-grid-body", true);

			await init(true);
			if (forceUpdateGridScrollPosition) updateGridScrollPosition(true);
		});
	};

	const updateGridScrollPosition = (force: boolean = false) => {
		const refreshedWithPagesLoaded =
			force ||
			(window.performance.getEntriesByType("navigation")[0]["type"] == "reload" && getParameter("currentpage"));
		history.scrollRestoration = refreshedWithPagesLoaded ? "manual" : "auto";

		if (refreshedWithPagesLoaded) {
			gridEl.scrollIntoView({ behavior: "auto", block: "start", inline: "start" });

			const url = updateParameter("currentpage", "", window.location.href);
			history.replaceState(null, "", url.toLowerCase());
		}
	};

	const toggleAllImportedMessage = (showMessage: boolean) => {
		showMessage
			? expressRemoveClass(toggleAllImportedMssEl, "u-hide")
			: expressAddClass(toggleAllImportedMssEl, "u-hide");

		if (gridBody === undefined || (allPagesLoaded() && getNrOfSelectableItems() == 0))
			gridHeaderControls.disableSelectAll();
		else gridHeaderControls.enableSelectAll();
	};

	const itemsLoadedEvent = async (pageLoadedResult: IPageLoadedResult) => {
		if (pageLoadedResult.isPageEmpty || pageLoadedResult.noMorePagesToLoad || allPagesLoaded()) {
			hideLoading();
			return;
		}

		// loadingEl must be fully visible. Partial visibility is handled by the infinite-scroll func
		if (isElementPartiallyVisibleInViewport(loadingEl, 100)) {
			await gridBody.loadNewPage();
		}

		itemsLoaded && itemsLoaded(storeType);
	};

	const showLoading = () => {
		expressRemoveClass(loadingEl, "u-hide");
	};
	const hideLoading = () => {
		expressAddClass(loadingEl, "u-hide");
	};

	const initInfiniteScroll = () => {
		if (!loadingEl) return;

		const currentPageSize = parseInt(gridEl.dataset.initpagesize, 10) || parseInt(gridBodyEl.dataset.initpagesize, 10);
		if (nrOfTotalItems != -1 && currentPageSize >= nrOfTotalItems) {
			return;
		}

		isObserver = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach(async (entry) => {
					if (entry?.isIntersecting ?? false) {
						await gridBody.loadNewPage();

						if (allPagesLoaded()) {
							hideLoading();
							observer.unobserve(loadingEl);
						}
					}
				});
			},
			{ rootMargin: "0px 0px 0px 0px", threshold: 1, root: null },
		);
		isObserver.observe(loadingEl);

		showLoading();
	};

	const initGridHeader = () => {
		if (!gridHeaderEl) return;

		gridHeaderControls = createGridHeaderControls(gridHeaderEl, gridEl, {
			onOrderPrints: onOrderPrints,
			onDelete: onDeleteEvent,
			onSort: onSort,
			onMoveToAnotherFolder: onMoveEvent,
			enableOrderPrintsButton,
			onImportedPhotosCheckboxChanged: () =>
				onImportedPhotosCheckboxChanged && onImportedPhotosCheckboxChanged(storeType),
			getNrOfSelectableItems,
			getNrOfVisuallySelectedItems,
			getNrOfSelectedItems,
			selectAllClicked: selectAllClicked,
			allPagesLoaded,
		});
	};

	const initGridBody = async (isRefresh = false) => {
		repositionGrid();

		gridBody = await createGridBody(gridBodyEl, {
			maxSelectableItems: maxSelectableItems ?? 0,
			isSelectAllActive: () => gridHeaderControls?.isSelectAllActive() && gridHeaderControls?.isSelectAllCssActive(),
			onItemClick: onItemClick && onItemClickEvent, // onItemClick not set > don't set onItemClick to allow for onItemSelect-fallback
			onItemsSelect: onItemsSelectEvent,
			onNewItemClick: onNewItemClickEvent,
			loadItems,
			getImportedPhotosIds: getImportedPhotosIds ? () => getImportedPhotosIds(storeType) : null,
			itemsLoaded: itemsLoadedEvent,
			nrOfTotalItems,
		});

		afterGridBodyLoaded && afterGridBodyLoaded(gridBody);

		// When refreshing: remove loadingEl and allImportedEl to prevent duplicates
		if (isRefresh) {
			if (loadingEl) loadingEl.remove();
			if (toggleAllImportedMssEl) toggleAllImportedMssEl.remove();
		}

		loadingEl = expressQuerySelector<HTMLElement>(gridEl, ".technical-loading");
		toggleAllImportedMssEl = expressQuerySelector<HTMLElement>(gridEl, ".technical-allimported", true);

		if (updateOrSetPageTitle && storeType != StoreType.eXpress && gridBodyEl.dataset.counttodisplay) {
			const label = gridBodyEl.dataset.counttodisplay.replace("{0}", gridBody?.getItems().length.toString() || "0");
			updateOrSetPageTitle(label);
		}

		if (fetchItemAmount) {
			const total = await fetchItemAmount();
			updateTotalItems(total);
			updateSelectAllState();
		}
	};

	const init = async (isRefresh = false) => {
		if (sourceHeader) {
			expressEventListener(window, "resize", repositionGrid);
		}

		if (!isRefresh) expressAddClass(gridBodyEl, "skeleton-loading");

		// Initialize gridBody first. GridHeader use some of the gridBody-methods.
		await initGridBody(isRefresh);
		initGridHeader();

		// No items > no loading
		if (gridBody.getItems().length > 0) initInfiniteScroll();

		expressRemoveClass(gridBodyEl, "skeleton-loading");

		itemsLoaded && itemsLoaded(storeType);
	};

	const dispose = () => {
		gridHeaderControls && gridHeaderControls.dispose();
		gridBody && gridBody.dispose();
		isObserver && isObserver.disconnect();
	};

	await init();

	return {
		dispose,
		refresh: (forceUpdateGridScrollPosition: boolean) => refresh(null, forceUpdateGridScrollPosition),
		getStoreType: () => storeType,
		getItems: () => gridBody.getItems(),
		enableItem: (item: HTMLElement) => gridBody.enableItem(item),
		disableItem: (item: HTMLElement) => gridBody.disableItem(item),
		isItemEnabled: (item: HTMLElement) => gridBody.isItemEnabled(item),
		isItemDisabled: (item: HTMLElement) => gridBody.isItemDisabled(item),
		getSelectedItems: () => gridBody.getSelectedItems(),
		getSelectedItemIds: () => gridBody.getSelectedItemIds(),
		isHideImportedChecked: () => gridHeaderControls?.isHideImportedChecked() ?? false,
		toggleAllImportedMessage,
		allPagesLoaded,
		updateGridScrollPosition,
	} as IGrid;
}
